import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../../contexts/AuthContext'
import { churchApi } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import {
  BuildingOfficeIcon,



  CheckCircleIcon,
  ClockIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

const churchRegistrationSchema = z.object({
  name: z.string().min(2, 'Church name must be at least 2 characters'),
  location: z.string().min(5, 'Please provide a detailed location'),
  contact_email: z.string().email('Invalid email address'),
  contact_phone: z.string().min(10, 'Please provide a valid phone number'),
  description: z.string().optional(),
  website: z.string().url('Invalid website URL').optional().or(z.literal('')),
  established_year: z.number().min(1800).max(new Date().getFullYear()).optional(),
  pastor_name: z.string().min(2, 'Pastor name is required'),
  additional_notes: z.string().optional()
})

type ChurchRegistrationForm = z.infer<typeof churchRegistrationSchema>

const ChurchRegistration: React.FC = () => {
  const { user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [churchStatus, setChurchStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ChurchRegistrationForm>({
    resolver: zodResolver(churchRegistrationSchema)
  })

  const onSubmit = async (data: ChurchRegistrationForm) => {
    setIsSubmitting(true)
    try {
      const churchData = {
        ...data,
        status: 'pending', // All new church registrations require approval
        coordinator_id: user?.id
      }

      await churchApi.createChurch(churchData)

      setHasSubmitted(true)
      setChurchStatus('pending')
      toast.success('Church registration submitted for approval!')
      reset()
    } catch (error: any) {
      console.error('Church registration failed:', error)
      toast.error(error.response?.data?.error || 'Failed to register church. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // If user already has a church, show status
  if (user?.churchId || hasSubmitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Church Registration Status</h2>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          {churchStatus === 'pending' && (
            <div className="text-center">
              <ClockIcon className="mx-auto h-8 w-8 text-yellow-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Registration Pending</h3>
              <p className="text-gray-600 mb-4">
                Your church registration has been submitted and is pending approval by an administrator.
                You will be notified once your church is approved.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm text-yellow-800">
                  <strong>What's next?</strong> An administrator will review your church registration
                  and contact you if any additional information is needed.
                </p>
              </div>
            </div>
          )}

          {churchStatus === 'approved' && (
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-8 w-8 text-green-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Church Approved!</h3>
              <p className="text-gray-600">
                Congratulations! Your church has been approved and is now active in the system.
              </p>
            </div>
          )}

          {churchStatus === 'rejected' && (
            <div className="text-center">
              <XCircleIcon className="mx-auto h-8 w-8 text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Registration Requires Updates</h3>
              <p className="text-gray-600 mb-4">
                Your church registration needs some updates. Please contact an administrator for details.
              </p>
              <button
                onClick={() => {
                  setHasSubmitted(false)
                  setChurchStatus(null)
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Submit New Registration
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Register Your Church</h1>
        <p className="text-gray-600">
          Please provide accurate information about your church. This registration will be reviewed by an administrator.
        </p>
      </div>

      {/* Registration Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Church Name *
                </label>
                <input
                  {...register('name')}
                  type="text"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., St. Mary Coptic Orthodox Church"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Location *
                </label>
                <input
                  {...register('location')}
                  type="text"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Full address including city, state"
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="pastor_name" className="block text-sm font-medium text-gray-700">
                  Pastor/Priest Name *
                </label>
                <input
                  {...register('pastor_name')}
                  type="text"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Fr. John Smith"
                />
                {errors.pastor_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.pastor_name.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700">
                  Contact Email *
                </label>
                <input
                  {...register('contact_email')}
                  type="email"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="church@example.com"
                />
                {errors.contact_email && (
                  <p className="mt-1 text-sm text-red-600">{errors.contact_email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700">
                  Contact Phone *
                </label>
                <input
                  {...register('contact_phone')}
                  type="tel"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
                {errors.contact_phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.contact_phone.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                  Website (Optional)
                </label>
                <input
                  {...register('website')}
                  type="url"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://www.yourchurch.com"
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="established_year" className="block text-sm font-medium text-gray-700">
                  Established Year (Optional)
                </label>
                <input
                  {...register('established_year', { valueAsNumber: true })}
                  type="number"
                  min="1800"
                  max={new Date().getFullYear()}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 1950"
                />
                {errors.established_year && (
                  <p className="mt-1 text-sm text-red-600">{errors.established_year.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Church Description (Optional)
              </label>
              <textarea
                {...register('description')}
                rows={4}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief description of your church, its history, and community..."
              />
            </div>

            <div className="mt-4">
              <label htmlFor="additional_notes" className="block text-sm font-medium text-gray-700">
                Additional Notes (Optional)
              </label>
              <textarea
                {...register('additional_notes')}
                rows={3}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional information you'd like to share with the administrators..."
              />
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  <strong>Please note:</strong> Your church registration will be reviewed by an administrator.
                  You'll receive an email notification once your church is approved. All information must be accurate and verifiable.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <LoadingSpinner size="sm" />
              ) : (
                'Submit Registration'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ChurchRegistration